/**
 * Circle tool metadata registration
 * Feature: 049-critique-photo-markup
 */

import React from 'react';
import type { MarkupShape, CircleShape } from '@/types/markup.types';
import { isCircleShape } from '@/types/markup.types';
import { registerTool } from '@/utils/markupToolRegistry';
import type { HandlePosition, ToolMetadata, ShapeRenderProps } from '@/utils/markupToolRegistry';
import { CircleTool } from './CircleTool';
import { CircleIcon } from '../ToolIcons';
import { isHandDrawn } from '@/utils/roughRenderer';
import { HandDrawnCircle } from '../HandDrawnShapes';

/**
 * Circle tool metadata
 */
const circleToolMetadata: ToolMetadata = {
  type: 'circle',
  label: 'Circle',
  icon: CircleIcon,
  Component: CircleTool,
  zOrder: 0, // Default z-order (render on top of background effects)

  /**
   * Get handle positions for circle
   * Handle 1: Center point (for moving)
   * Handle 2: Radius point (for resizing)
   */
  getHandles: (shape: MarkupShape): HandlePosition[] => {
    if (!isCircleShape(shape)) return [];

    return [
      {
        id: 'handle1',
        x: shape.geometry.cx,
        y: shape.geometry.cy,
        cursor: 'move',
        label: 'Move circle center',
      },
      {
        id: 'handle2',
        x: shape.geometry.cx + shape.geometry.r,
        y: shape.geometry.cy,
        cursor: 'ew-resize',
        label: 'Resize circle radius',
      },
    ];
  },

  /**
   * Update circle from handle drag
   *
   * BUG FIX (#480): Now uses image-relative coordinates (0-100 for both X and Y)
   */
  updateFromHandle: (
    shape: MarkupShape,
    handleId: 'handle1' | 'handle2',
    x: number,
    y: number,
    _viewBoxHeight: number // Kept for backwards compatibility, no longer used
  ): MarkupShape => {
    if (!isCircleShape(shape)) return shape;

    if (handleId === 'handle1') {
      // Dragging center - move the circle (image-relative: 0-100)
      return {
        ...shape,
        geometry: {
          cx: Math.max(0, Math.min(100, x)),
          cy: Math.max(0, Math.min(100, y)),
          r: shape.geometry.r,
        },
      } as CircleShape;
    } else {
      // Dragging radius handle - adjust radius
      const dx = x - shape.geometry.cx;
      const dy = y - shape.geometry.cy;
      const newRadius = Math.sqrt(dx * dx + dy * dy);
      return {
        ...shape,
        geometry: {
          cx: shape.geometry.cx,
          cy: shape.geometry.cy,
          r: Math.max(1, Math.min(50, newRadius)),
        },
      } as CircleShape;
    }
  },

  /**
   * Translate circle by deltaX and deltaY
   *
   * BUG FIX (#480): Now uses image-relative coordinates (0-100 for both X and Y)
   */
  translateShape: (
    shape: MarkupShape,
    deltaX: number,
    deltaY: number,
    _viewBoxHeight: number // Kept for backwards compatibility, no longer used
  ): MarkupShape => {
    if (!isCircleShape(shape)) return shape;

    return {
      ...shape,
      geometry: {
        cx: Math.max(0, Math.min(100, shape.geometry.cx + deltaX)),
        cy: Math.max(0, Math.min(100, shape.geometry.cy + deltaY)),
        r: shape.geometry.r,
      },
    } as CircleShape;
  },

  /**
   * Render circle shape as SVG
   */
  renderShape: ({ shape, isSelected, onMouseDown, viewBoxHeight }: ShapeRenderProps): React.ReactElement => {
    if (!isCircleShape(shape)) return React.createElement('g');

    // Calculate responsive fontSize based on smaller dimension (width=100 or height=viewBoxHeight)
    // This ensures consistent label size regardless of image aspect ratio
    const smallerDimension = Math.min(100, viewBoxHeight);
    const fontSize = smallerDimension * 0.04; // 4% of smaller dimension
    const labelRadius = smallerDimension * 0.03; // 3% of smaller dimension
    const labelOffset = labelRadius + 1; // Offset to move label away from shape edge

    // Calculate image center
    const imageCenterX = 50;
    const imageCenterY = viewBoxHeight / 2;

    // Calculate angle from circle center to image center
    const dx = imageCenterX - shape.geometry.cx;
    const dy = imageCenterY - shape.geometry.cy;
    const angle = Math.atan2(dy, dx);

    // Position label on circle circumference pointing toward image center, offset outward
    const labelDistance = shape.geometry.r + labelOffset;
    const labelX = shape.geometry.cx + labelDistance * Math.cos(angle);
    const labelY = shape.geometry.cy + labelDistance * Math.sin(angle);
    const className = `markup-shape ${isSelected ? 'selected' : ''}`;
    const style: React.CSSProperties = {
      cursor: isSelected ? 'grab' : 'pointer',
      pointerEvents: 'auto',
    };

    // Check if hand-drawn style is enabled
    const useHandDrawn = isHandDrawn(shape.strokeDasharray);

    return React.createElement(
      'g',
      { key: shape.id },
      // Circle shape (hand-drawn or standard)
      useHandDrawn
        ? React.createElement(HandDrawnCircle, {
            cx: shape.geometry.cx,
            cy: shape.geometry.cy,
            r: shape.geometry.r,
            strokeColor: shape.strokeColor,
            strokeWidth: shape.strokeWidth,
            isSelected,
            onMouseDown,
            className,
          })
        : React.createElement('circle', {
            className,
            cx: shape.geometry.cx,
            cy: shape.geometry.cy,
            r: shape.geometry.r,
            stroke: shape.strokeColor,
            strokeWidth: isSelected ? shape.strokeWidth * 1.5 : shape.strokeWidth,
            strokeDasharray: shape.strokeDasharray,
            fill: 'none',
            vectorEffect: 'non-scaling-stroke',
            style,
            onMouseDown,
          }),
      // Shape number label background
      React.createElement('circle', {
        cx: labelX,
        cy: labelY,
        r: labelRadius,
        fill: 'black',
        fillOpacity: '0.5',
        stroke: 'white',
        strokeWidth: '0.3',
        vectorEffect: 'non-scaling-stroke',
        style,
        onMouseDown,
      }),
      // Shape number label text
      React.createElement(
        'text',
        {
          x: labelX,
          y: labelY,
          fontSize: String(fontSize),
          fontWeight: 'bold',
          fill: 'white',
          textAnchor: 'middle',
          dominantBaseline: 'central',
          style: { pointerEvents: 'none', userSelect: 'none' },
        },
        shape.number
      )
    );
  },
};

// Register the tool
registerTool(circleToolMetadata);

export default circleToolMetadata;
